import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { ApiHelper } from '../../apiHelper/ApiHelper';
import { stripePromise } from '../../configurable/stripe';
import CheckoutComponent from '../../components/CheckoutComponent';
import BankTransferInstructions, { BankTransferInfo } from '../../components/BankTransferInstructions';
import { formatPrice } from '../../components/ProductCatalog';
import './styles/Payment.scss';

type Installment = {
    id: string;
    product: number;
    sequence: number;
    amount: number;
    dueDate: string;
    paid: boolean;
    // 0 = card (key carries the Stripe secret), 1 = bank transfer (paid off-platform).
    paymentMethod: number;
    key: string;
};

// The card/transfer details a GetInstallment / switch response carries live in one place: the
// Stripe client secret (card) or the account/VS/QR bundle (transfer). Only one is ever set.
function bankInfoFrom(proc: any): BankTransferInfo {
    return {
        amount: proc.amount,
        variableSymbol: proc.variableSymbol,
        iban: proc.iban,
        accountDisplay: proc.accountDisplay,
        qrCode: proc.qrCode,
        installmentId: proc.id,
        invoiceId: proc.invoiceId,
    };
}

const productNames: { [key: number]: string } = {
    0: "Samostudium",
    1: "Kurz s lektorem",
    2: "Individuální lekce",
    3: "Hodiny navíc",
};

export default function Payment() {
    const { installmentId } = useParams();
    const [installment, setInstallment] = useState<Installment | null>(null);
    const [loading, setLoading] = useState(true);
    const [paid, setPaid] = useState(false);
    const [clientSecret, setClientSecret] = useState("");
    const [bankInfo, setBankInfo] = useState<BankTransferInfo | null>(null);
    const [switching, setSwitching] = useState(false);

    const api = new ApiHelper();
    const loggedIn = localStorage.getItem("token") !== null;

    function formatDate(d : string) {
        const date = new Date(d);
        return date.getDate() + ". " + (date.getMonth() + 1) + ". " + date.getFullYear();
    }

    async function fetch() {
        if (!installmentId) return;

        try {
            const res = await api.ordersApi.getInstallmentDetail(installmentId);
            const proc = await res.json();
            setInstallment(proc);
            setPaid(proc.paid);
            if (proc.paymentMethod === 0 && proc.key) setClientSecret(proc.key);
            else if (proc.paymentMethod === 1) setBankInfo(bankInfoFrom(proc));
        } catch (x : any) {
            api.processErrors(x);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        fetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // The card ⇄ transfer toggle: each switch flips the installment server-side and tears down the
    // other method's artifact (cancel the Stripe intent / mint a fresh one), so only one method is
    // ever payable at a time — same flow the checkout confirmation screen uses.
    async function payByCard() {
        if (!installmentId || switching) return;
        setSwitching(true);

        try {
            const res = await api.ordersApi.switchToCardCreate(installmentId);
            const proc = await res.json();
            if (proc.key) { setBankInfo(null); setClientSecret(proc.key); }
        } catch (x : any) {
            api.processErrors(x);
        } finally {
            setSwitching(false);
        }
    }

    async function payByTransfer() {
        if (!installmentId || switching) return;
        setSwitching(true);

        try {
            const res = await api.ordersApi.switchToBankTransferCreate(installmentId);
            const proc = await res.json();
            setClientSecret("");
            setBankInfo(bankInfoFrom(proc));
        } catch (x : any) {
            api.processErrors(x);
        } finally {
            setSwitching(false);
        }
    }

    return (
        <div className="payment">
            <div className="paymentCard">
                {loading ?
                    <div className="missing">Načítání…</div>
                    : !installment ?
                    <div className="doneCard">
                        <h2>Platba nenalezena</h2>
                    </div>
                    : paid ?
                    <div className="doneCard">
                        <div className="check">✓</div>
                        <h2>Zaplaceno</h2>
                        <p>Tato platba je uhrazena. Děkujeme!</p>
                    </div>
                    :
                    <>
                        <h1 className="title">Úhrada platby</h1>

                        <div className="detail">
                            <div className="row"><span>Produkt</span><b>{productNames[installment.product] ?? "Produkt"}</b></div>
                            <div className="row"><span>Platba č.</span><b>{installment.sequence}</b></div>
                            <div className="row"><span>Splatnost</span><b>{formatDate(installment.dueDate)}</b></div>
                            <div className="row total"><span>K úhradě</span><b>{formatPrice(installment.amount)}</b></div>
                        </div>

                        {clientSecret !== "" ?
                            <>
                                <Elements stripe={stripePromise} options={{clientSecret: clientSecret}}>
                                    <CheckoutComponent clientSecret={clientSecret} onSuccess={() => setPaid(true)} />
                                </Elements>
                                <button type="button" className="switchMethod" disabled={switching} onClick={payByTransfer}>
                                    {switching ? "Načítání…" : "Zaplatit převodem místo toho"}
                                </button>
                            </>
                            : bankInfo &&
                            <>
                                <p className="transferNote">Tuto platbu hraď bankovním převodem podle údajů níže (číslo účtu, variabilní symbol a QR kód), které jsme ti poslali i e-mailem. Jakmile platbu obdržíme, platbu označíme jako uhrazenou.</p>
                                <BankTransferInstructions
                                    info={bankInfo}
                                    canDownload={loggedIn}
                                    onPayByCard={payByCard}
                                    payingByCard={switching}
                                />
                            </>
                        }
                    </>
                }
            </div>
        </div>
    );
}
